import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Box, Button, Paper, Typography } from '@material-ui/core'
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowData,
} from '@mui/x-data-grid'
import { useProjectsQuery } from '../../generated/graphql'
import { useProjects } from '../../project/use-projects'
import { formatDistance } from '../common/util/date-util'
import { getDataGridLocale } from '../../util/data-grid-util'

const PAGE_SIZE = 5

export function DeletedProjectsPage(): React.ReactElement {
  const { t } = useTranslation()
  const [offset, setOffset] = useState(0)
  const { data, loading } = useProjectsQuery({
    variables: {
      filter: { offset, count: PAGE_SIZE, isDeleted: true },
    },
  })
  const { restoreProject } = useProjects()

  const totalCount = data?.projects.totalCount ?? 0
  const projects = data?.projects.items ?? []

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('deletedProjects.name'),
      flex: 2,
      sortable: false,
    },
    {
      field: 'owner',
      headerName: t('deletedProjects.owner'),
      flex: 2,
      sortable: false,
    },
    {
      field: 'deletedAt',
      headerName: t('deletedProjects.deletedAt'),
      flex: 1,
      sortable: false,
    },
    {
      field: 'actions',
      headerName: ' ',
      sortable: false,
      renderCell: ({ row }: GridRenderCellParams) => (
        <Button
          variant="contained"
          color="primary"
          size="small"
          data-cy="restore-project"
          onClick={() => restoreProject(row.id as string)}
        >
          {t('deletedProjects.restore')}
        </Button>
      ),
    },
  ]

  const rows: GridRowData[] = projects.map((project) => ({
    id: project.id,
    name: project.name,
    owner: project.owner.displayName,
    deletedAt: project.deletedAt ? formatDistance(project.deletedAt) : '',
  }))

  return (
    <Box>
      <Helmet title={t('deletedProjects.title')} />

      <Typography variant="h3">{t('deletedProjects.title')}</Typography>

      <Box component={Paper} my={2}>
        <DataGrid
          columns={columns}
          rows={rows}
          paginationMode="server"
          loading={loading}
          rowCount={totalCount}
          pageSize={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          localeText={getDataGridLocale(PAGE_SIZE)}
          autoHeight
          pagination
          disableColumnMenu
          disableSelectionOnClick
          hideFooterSelectedRowCount
          onPageChange={(page) => setOffset(page * PAGE_SIZE)}
        />
      </Box>
    </Box>
  )
}
